'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, ArrowRight, UserPlus, LogIn, CheckCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useSiteLogo, FALLBACK_LOGO } from '@/lib/useSiteLogo';

type LoginRole = 'client' | 'staff' | 'admin' | 'superadmin';
type AuthMode = 'login' | 'register';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface RegisterForm {
  firstName: string;
  lastName: string;
  organization: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const roleRoutes: Record<LoginRole, string> = {
  client: '/activity-feed',
  staff: '/activity-feed',
  admin: '/activity-feed',
  superadmin: '/activity-feed'
};

const REDIRECT_STORAGE_KEY = 'celsoft_redirect_settings';

const defaultRedirects = {
  loginRedirect: '/activity-feed',
  registerRedirect: '/activity-feed',
  logoutRedirect: '/',
};

export default function AuthPage() {
  const router = useRouter();
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginData, setLoginData] = useState<LoginForm>({ email: '', password: '', remember: false });
  const [registerData, setRegisterData] = useState<RegisterForm>({
    firstName: '', lastName: '', organization: '', email: '', phone: '', password: '', confirmPassword: '', terms: false
  });
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [redirects, setRedirects] = useState(defaultRedirects);
  const { logoUrl, isCustomLogo } = useSiteLogo();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(REDIRECT_STORAGE_KEY);
        if (stored) {
          setRedirects({ ...defaultRedirects, ...JSON.parse(stored) });
        }
      } catch {
        // ignore
      }
    }
  }, []);

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      });
      if (error) {
        setLoginError(error.message || 'Invalid credentials. Please check your email and password.');
        return;
      }
      // Fetch user role from user_profiles to determine redirect
      const userId = data.user?.id;
      let userRole: LoginRole = 'client';
      if (userId) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', userId)
          .maybeSingle();
        if (profile?.role) {
          const r = profile.role as string;
          if (r === 'superadmin' || r === 'super_admin') userRole = 'superadmin';
          else if (r === 'admin') userRole = 'admin';
          else if (r === 'staff') userRole = 'staff';
          else userRole = 'client';
        }
      }
      router.push(roleRoutes[userRole] || redirects.loginRedirect);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign in failed. Please try again.';
      setLoginError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError('');
    if (registerData.password !== registerData.confirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const fullName = `${registerData.firstName} ${registerData.lastName}`.trim();

      // 1. Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: registerData.email,
        password: registerData.password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });
      if (authError) throw authError;

      const userId = authData.user?.id;
      if (userId) {
        // 2. Upsert user_profiles row — always assign 'client' role on public registration
        const { error: profileError } = await supabase.from('user_profiles').upsert({
          id: userId,
          email: registerData.email,
          full_name: fullName,
          phone: registerData.phone || '',
          company: registerData.organization || '',
          role: 'client',
        }, { onConflict: 'id' });

        if (profileError) {
          console.warn('Profile upsert warning:', profileError.message);
        }

        // 3. Ensure new_registrations entry exists
        const { error: regCheckError, data: existingReg } = await supabase
          .from('new_registrations')
          .select('id')
          .eq('email', registerData.email)
          .maybeSingle();

        if (!regCheckError && !existingReg) {
          await supabase.from('new_registrations').insert({
            user_profile_id: userId,
            full_name: fullName,
            email: registerData.email,
            phone: registerData.phone || '',
            company: registerData.organization || '',
            job_title: '',
            status: 'pending',
          });
        } else if (!regCheckError && existingReg) {
          await supabase.from('new_registrations').update({
            full_name: fullName,
            phone: registerData.phone || '',
            company: registerData.organization || '',
          }).eq('id', existingReg.id);
        }
      }

      // 4. Auto sign-in immediately after registration
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: registerData.email,
        password: registerData.password,
      });

      if (signInError) {
        setRegisterSuccess(true);
      } else {
        router.push(redirects.registerRedirect || '/activity-feed');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setRegisterError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 md:p-10"
      style={{ background: '#ecf2f6' }}>
      
      {/* Background decorations */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(600px at 30% 20%, rgba(30,82,94,0.06), transparent 80%)' }} />
      
      <div
        className="fixed inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(30,82,94,1) 1px, transparent 1px), linear-gradient(90deg, rgba(30,82,94,1) 1px, transparent 1px)',
          backgroundSize: '64px 64px'
        }} />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-up" style={{ opacity: 0, animationDelay: '0.05s' }}>
          <Link href="/" className="inline-flex items-center justify-center gap-3">
            <img
              src={logoUrl || FALLBACK_LOGO}
              alt="Celsoft Corporation"
              className="h-auto w-auto object-contain"
              style={{
                maxHeight: isCustomLogo ? '600px' : '240px',
                maxWidth: isCustomLogo ? '2160px' : '240px',
              }}
            />
          </Link>
          <p className="text-xs text-[#9CA3AF] mt-2 uppercase tracking-widest">Client &amp; Staff Portal</p>
        </div>

        {/* Card */}
        <div className="bento-card p-8 animate-fade-up" style={{ opacity: 0, animationDelay: '0.15s' }}>
          {/* Mode toggle */}
          <div
            className="flex rounded-full overflow-hidden mb-6 p-1"
            style={{ background: 'rgba(30,82,94,0.06)', border: '1px solid rgba(30,82,94,0.12)' }}>
            
            <button
              onClick={() => setMode('login')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold rounded-full transition-all ${
              mode === 'login' ? 'text-white' : 'text-[#71717A] hover:text-[#111111]'}`}
              style={{
                background: mode === 'login' ? 'linear-gradient(135deg, #1e525e, #2d7a8a)' : 'transparent',
                boxShadow: mode === 'login' ? '0 2px 8px rgba(30,82,94,0.3)' : 'none'
              }}>
              <LogIn size={14} /> Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold rounded-full transition-all ${
              mode === 'register' ? 'text-white' : 'text-[#71717A] hover:text-[#111111]'}`}
              style={{
                background: mode === 'register' ? 'linear-gradient(135deg, #1e525e, #2d7a8a)' : 'transparent',
                boxShadow: mode === 'register' ? '0 2px 8px rgba(30,82,94,0.3)' : 'none'
              }}>
              <UserPlus size={14} /> Register
            </button>
          </div>

          {mode === 'register' && registerSuccess ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(141,197,62,0.12)' }}>
                <CheckCircle size={28} style={{ color: '#6fa030' }} />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#111111] mb-1">Registration Successful!</h3>
                <p className="text-sm text-[#71717A] leading-relaxed">
                  Your account has been created. You can now sign in.
                </p>
              </div>
              <button
                onClick={() => { setMode('login'); setRegisterSuccess(false); setRegisterData({ firstName: '', lastName: '', organization: '', email: '', phone: '', password: '', confirmPassword: '', terms: false }); }}
                className="btn-brand px-6 py-2.5 text-sm">
                Go to Sign In
              </button>
            </div>
          ) : mode === 'login' ? (
            <form onSubmit={onLogin} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#111111] mb-1.5">Email Address</label>
                <input
                  type="email"
                  required
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
                  style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                  placeholder="your@email.com" />
              </div>

              <div>
                <div className="flex justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-[#111111]">Password</label>
                  <a href="#" className="text-xs text-[#1e525e] hover:text-[#2d7a8a] transition-colors">
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl outline-none transition-all"
                    style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                    placeholder="••••••••" />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111111] transition-colors">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  checked={loginData.remember}
                  onChange={(e) => setLoginData({ ...loginData, remember: e.target.checked })}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: '#1e525e' }} />
                <label htmlFor="remember" className="text-xs text-[#71717A]">
                  Keep me signed in for 30 days
                </label>
              </div>

              {loginError && (
                <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-xl">{loginError}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-brand w-full py-3 text-sm disabled:opacity-60">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <><span>Sign In</span> <ArrowRight size={15} /></>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={onRegister} className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#111111] mb-1.5">First Name *</label>
                  <input
                    type="text"
                    required
                    value={registerData.firstName}
                    onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
                    style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                    placeholder="First name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#111111] mb-1.5">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={registerData.lastName}
                    onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
                    style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                    placeholder="Last name" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#111111] mb-1.5">Organization *</label>
                <input
                  type="text"
                  required
                  value={registerData.organization}
                  onChange={(e) => setRegisterData({ ...registerData, organization: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
                  style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                  placeholder="Your organization" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#111111] mb-1.5">Email Address *</label>
                <input
                  type="email"
                  required
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
                  style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                  placeholder="your@email.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#111111] mb-1.5">Phone</label>
                <input
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm rounded-xl outline-none transition-all"
                  style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                  placeholder="+263 ..." />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#111111] mb-1.5">Password *</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl outline-none transition-all"
                    style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                    placeholder="Min. 6 characters" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#111111] mb-1.5">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPw ? 'text' : 'password'}
                    required
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl outline-none transition-all"
                    style={{ background: '#F3F3F6', border: '1px solid rgba(30,82,94,0.18)' }}
                    placeholder="Repeat password" />
                  <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                    {showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  checked={registerData.terms}
                  onChange={(e) => setRegisterData({ ...registerData, terms: e.target.checked })}
                  className="w-4 h-4 mt-0.5 rounded"
                  style={{ accentColor: '#1e525e' }} />
                <label htmlFor="terms" className="text-xs text-[#71717A] leading-relaxed">
                  I agree to the{' '}
                  <a href="#" className="text-[#1e525e] hover:underline">Terms of Service</a>{' '}and{' '}
                  <a href="#" className="text-[#1e525e] hover:underline">Privacy Policy</a>
                </label>
              </div>
              {registerError && (
                <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-xl">{registerError}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="btn-brand w-full py-3 text-sm disabled:opacity-60">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer link */}
        <p className="text-center text-xs text-[#9CA3AF] mt-6">
          <Link href="/" className="hover:text-[#1e525e] transition-colors">
            ← Back to Celsoft Corporation
          </Link>
        </p>
      </div>
    </div>
  );
}